/**
 * First-time setup wizard shell.
 *
 * Owns the card chrome (logo + MUI Stepper header) and the step state machine;
 * each step renders its own body + footer into the card and drives navigation
 * through the `goNext` / `goBack` / `onComplete` callbacks. Shared, mutable
 * `ctx` carries data forward (connectivity result, credentials, sync result…).
 *
 * Login is placed BEFORE the data download because every master-data endpoint
 * requires the auth token that only login produces.
 */

import React, { useCallback, useState } from 'react';
import { Box, Paper, Stepper, Step, StepLabel, Typography } from '@mui/material';
import logo from '../assets/images/logo.png';
import sx from './setupStyles';

import Welcome from './steps/Welcome';
import Connectivity from './steps/Connectivity';
import Database from './steps/Database';
import Login from './steps/Login';
import Sync from './steps/Sync';
import Verify from './steps/Verify';
import Complete from './steps/Complete';

const STEPS = [
  { key: 'welcome', label: 'Welcome', Component: Welcome },
  { key: 'connectivity', label: 'Connection', Component: Connectivity },
  { key: 'database', label: 'Database', Component: Database },
  { key: 'login', label: 'Sign in', Component: Login },
  { key: 'sync', label: 'Download', Component: Sync },
  { key: 'verify', label: 'Verify', Component: Verify },
  { key: 'complete', label: 'Done', Component: Complete },
];

export default function SetupWizard({ onComplete }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [ctx, setCtx] = useState({});

  // Stable identities: steps depend on these in useEffect/useCallback, so an
  // unstable identity would re-trigger their effects on every render (re-running
  // sync/verify/connectivity endlessly). Functional updates keep deps empty.
  const goNext = useCallback(() => setStepIndex((i) => Math.min(i + 1, STEPS.length - 1)), []);
  const goBack = useCallback(() => setStepIndex((i) => Math.max(i - 1, 0)), []);
  const patchCtx = useCallback((partial) => setCtx((c) => ({ ...c, ...partial })), []);

  const { Component } = STEPS[stepIndex];

  return (
    <Box sx={sx.root}>
      <Paper elevation={4} sx={sx.card}>
        <Box sx={sx.header}>
          <img src={logo} alt="Younote" style={sx.logo} />
          <Stepper activeStep={stepIndex} alternativeLabel>
            {STEPS.map((s) => (
              <Step key={s.key}>
                <StepLabel>{s.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Step {stepIndex + 1} of {STEPS.length}
          </Typography>
        </Box>

        <Component
          ctx={ctx}
          patchCtx={patchCtx}
          goNext={goNext}
          goBack={goBack}
          onComplete={onComplete}
        />
      </Paper>
    </Box>
  );
}
